import ItiButton from "@/iti-core-react/components/ItiButton/ItiButton";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { AutomationDto } from "@/shared/dtos/automation-dto";
import { AutomationStatus } from "@/shared/enums/automation-status";
import { ProtocolType } from "@/shared/enums/protocol-type";
import { formatTimestampWithLocale } from "@/utils/format-utils";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AutomationCard.scss";

interface AutomationCardProps {
	automation: AutomationDto;
	onPlay: () => Promise<void>;
	onPause: () => Promise<void>;
	onStop: () => Promise<void>;
	onResume: () => Promise<void>;
	onViewDetail?: () => void;
}

export default function AutomationCard({
	automation,
	onPlay,
	onPause,
	onStop,
	onResume,
	onViewDetail,
}: AutomationCardProps) {
	const { t, language } = useLanguage();
	const navigate = useNavigate();
	const [processingAction, setProcessingAction] = useState<null | string>(null);

	const getStatusDotClass = () => {
		const statusCode = automation.status?.code;

		switch (statusCode) {
			case AutomationStatus.PENDING: // "PEN"
				return "pending";
			case AutomationStatus.STARTED: // "INI"
				return "started";
			case AutomationStatus.PAUSED: // "PAU"
				return "paused";
			case AutomationStatus.FINISHED: // "FIN"
				return "finished";
			case AutomationStatus.DELETED: // "DEL"
				return "deleted";
			case AutomationStatus.ERROR_NOT_ENOUGH_QUOTE: // "EQ1"
			case AutomationStatus.ERROR_PAUSED_EXHAUSTED_QUOTA: // "EQ2"
			case AutomationStatus.ERROR_CONFIGURING_POOL_PROCESSES: // "EP1"
				return "error";
			default:
				return "unknown";
		}
	};

	const StreamingTypes = [
		ProtocolType.KAFKA,
		ProtocolType.MQTT,
		ProtocolType.OPCUA,
	];
	const isStreaming = StreamingTypes.includes(
		automation.sourceConfiguration?.sourceType as ProtocolType,
	);
	const executeAction = async (action: () => Promise<void>, label: string) => {
		setProcessingAction(label);
		try {
			await action();
		} finally {
			setProcessingAction(null);
		}
	};

	const translatedStatus = useMemo(() => {
		switch (automation.status?.code) {
			case AutomationStatus.PENDING:
				return t("automation.status.pending");
			case AutomationStatus.STARTED:
				return t("automation.status.started");
			case AutomationStatus.PAUSED:
				return t("automation.status.paused");
			case AutomationStatus.FINISHED:
				return t("automation.status.finished");
			case AutomationStatus.DELETED:
				return t("automation.status.deleted");
			case AutomationStatus.ERROR_NOT_ENOUGH_QUOTE:
			case AutomationStatus.ERROR_PAUSED_EXHAUSTED_QUOTA:
			case AutomationStatus.ERROR_CONFIGURING_POOL_PROCESSES:
				return t("automation.error");
			default:
				return "-";
		}
	}, [automation.status?.code, t]);

	const isError = useMemo(() => {
		return [
			AutomationStatus.ERROR_NOT_ENOUGH_QUOTE,
			AutomationStatus.ERROR_PAUSED_EXHAUSTED_QUOTA,
			AutomationStatus.ERROR_CONFIGURING_POOL_PROCESSES,
		].includes(automation.status?.code as AutomationStatus);
	}, [automation.status?.code]);

	const showButton = (status: AutomationStatus[]) =>
		status.includes(automation.status?.code as AutomationStatus);

	const getConnectorLabel = () => {
		const type = automation.sourceConfiguration?.sourceType;

		if (type === "QUALITY_RULE") {
			return t("automation.quality-rule-card");
		}
		if (type === "PROFILING") {
			return t("automation.profiling-card");
		}

		const lowerType = type?.toLowerCase?.();
		return lowerType
			? lowerType.charAt(0).toUpperCase() + lowerType.slice(1)
			: "-";
	};

	const getFrequencyLabel = () => {
		const consolidation = automation.sourceConfiguration?.consolidation;
		if (!consolidation || consolidation.consolidation.value == null) return "-";

		return consolidation.consolidation.type === "TIME"
			? `${consolidation.consolidation.value} h`
			: `${consolidation.consolidation.value} MB`;
	};

	const getFrequencyIcon = () => {
		const type =
			automation.sourceConfiguration?.consolidation?.consolidation.type;
		if (type === "TIME") return "mdi-clock-time-ten-outline";
		if (type === "SIZE") return "mdi-file-outline";
		return "mdi-help-circle-outline";
	};

	const getFrequencyTooltip = () => {
		const type =
			automation.sourceConfiguration?.consolidation?.consolidation?.type;
		if (type === "TIME") return t("automation.consolidation.type.time");
		if (type === "SIZE") return t("automation.consolidation.type.size");
		return "";
	};

	const getAutomationTypeIcon = () => {
		const sourceType = automation.sourceConfiguration?.sourceType;

		if (!sourceType) return "mdi-help-circle-outline";

		if (
			[ProtocolType.KAFKA, ProtocolType.MQTT, ProtocolType.OPCUA].includes(
				sourceType as ProtocolType,
			)
		) {
			return "mdi-broadcast";
		}

		if (sourceType === "QUALITY_RULE") {
			return "mdi-shield-check-outline";
		}

		if (sourceType === "PROFILING") {
			return "mdi-chart-line";
		}

		return "mdi-database-sync-outline";
	};

	const getAutomationTypeTooltip = () => {
		const sourceType = automation.sourceConfiguration?.sourceType;

		if (!sourceType) return t("automation.type.unknown");

		if (
			[ProtocolType.KAFKA, ProtocolType.MQTT, ProtocolType.OPCUA].includes(
				sourceType as ProtocolType,
			)
		) {
			return t("automation.type.streaming");
		}

		if (sourceType === "QUALITY_RULE") {
			return t("automation.type.qualityRule");
		}

		if (sourceType === "PROFILING") {
			return t("automation.type.profiling");
		}

		return t("automation.type.database");
	};

	const getTypeSpecificInfo = () => {
		const sourceType = automation.sourceConfiguration?.sourceType;

		switch (sourceType) {
			case "POSTGRESQL":
				return [
					{
						icon: "mdi-database",
						label: `${automation.sourceConfiguration?.database}/${automation.sourceConfiguration?.schema}`,
						tooltip: t("automation.database.info"),
					},
					{
						icon: "mdi-server-network",
						label: `${automation.sourceConfiguration?.url}:${automation.sourceConfiguration?.port}`,
						tooltip: t("automation.server.info"),
					},
				];
			case "MQTT":
			case "KAFKA":
			case "OPCUA": {
				const items = [
					{
						icon: "mdi-server-network",
						label: `${automation.sourceConfiguration?.url}:${automation.sourceConfiguration?.port}`,
						tooltip: t("automation.server.info"),
					},
				];
				if (automation.sourceConfiguration?.topic) {
					items.push({
						icon: "mdi-tag",
						label: automation.sourceConfiguration.topic,
						tooltip: t("automation.topic.info"),
					});
				}
				return items;
			}
			case "QUALITY_RULE": {
				const executionData = automation.sourceConfiguration?.executionData;
				return [
					{
						icon: "mdi-chart-bar",
						label: executionData?.metricName || "-",
						tooltip: t("automation.metric.info"),
					},
					{
						icon: "mdi-table-column",
						label: executionData?.columnName || "-",
						tooltip: t("automation.column.info"),
					},
				];
			}
			case "PROFILING":
				return [
					{
						icon: "mdi-file-chart",
						label: t("automation.profiling.execution"),
						tooltip: t("automation.profiling.info"),
					},
				];
			default:
				return [];
		}
	};

	const hasAvailableActions = () => {
		const hasPlayButton = showButton([
			AutomationStatus.PENDING,
			AutomationStatus.ERROR_NOT_ENOUGH_QUOTE,
		]);
		const hasPauseButton = showButton([AutomationStatus.STARTED]);
		const hasStopButton = showButton([
			AutomationStatus.STARTED,
			AutomationStatus.PAUSED,
		]);
		const hasResumeButton = showButton([
			AutomationStatus.PAUSED,
			AutomationStatus.ERROR_PAUSED_EXHAUSTED_QUOTA,
		]);

		return hasPlayButton || hasPauseButton || hasStopButton || hasResumeButton;
	};

	const handleDatasetReferenced = useCallback(
		(data: any) => {
			navigate(`/automations/${data.sessionId}/catalogue/${data.datasetId}`, {
				state: { fromList: true },
			});
		},
		[navigate],
	);

	const getErrorMessage = () => {
		switch (automation.status?.code) {
			case AutomationStatus.ERROR_NOT_ENOUGH_QUOTE: // EQ1
				return t("automation.errors.EQ1");
			case AutomationStatus.ERROR_PAUSED_EXHAUSTED_QUOTA: // EQ2
				return t("automation.errors.EQ2");
			case AutomationStatus.ERROR_CONFIGURING_POOL_PROCESSES: // EP1
				return t("automation.errors.EP1");
			default:
				return automation.status?.description || t("automation.error");
		}
	};

	return (
		<div className="content-card-automations automation-card">
			<div className="row">
				<div className="col me-2">
					<div className="row d-flex">
						{/* Titulo y detalles */}
						<div className="col align-self-center">
							<div
								className="automation-card-title"
								title={automation.sessionName}
							>
								{automation.sessionName}
							</div>
							<div className="content-card-tech mt-0">
								{getConnectorLabel()}
							</div>
						</div>

						{/* Icono */}
						<div className="col-auto align-self-center ">
							<div
								className="content-card-icon-automation"
								title={getAutomationTypeTooltip()}
							>
								<i className={`mdi ${getAutomationTypeIcon()}`} />
							</div>
						</div>
					</div>

					<div
						className={`row mt-2 ${hasAvailableActions() ? "row-with-buttons" : ""}`}
					>
						{/* Indicador de estado*/}
						<div className={hasAvailableActions() ? "col-auto ps-4" : "col"}>
							<div className="d-flex align-items-center">
								<div
									className={`automation-status-chip ${automation.status?.code?.toLowerCase()}`}
									title={!isError ? t("automation.status-field") : undefined}
								>
									<div className="">{translatedStatus}</div>
								</div>
								{automation.status?.code && (
									<div className="automation-status-indicator">
										<span
											className={`status-dot ${getStatusDotClass()} ${automation.status.code === AutomationStatus.STARTED ? "pulse" : ""}`}
											title={`Status: ${automation.status.code} (${getStatusDotClass()})`}
										/>
									</div>
								)}
							</div>
						</div>

						{/* Actions - Only show if there are available actions */}
						{hasAvailableActions() && (
							<div className="col row p-0 m-0s">
								{showButton([
									AutomationStatus.PENDING,
									AutomationStatus.ERROR_NOT_ENOUGH_QUOTE,
								]) && (
									<div className="col-auto p-0 m-0 d-flex align-items-start">
										<ItiButton
											icon="mdi mdi-play"
											hint={t("automation.actions.play.tooltip")}
											disabled={!!processingAction}
											onClick={() =>
												executeAction(onPlay, t("automation.executing.play"))
											}
											elementAttr={{ class: "btn btn-sm btn-transparent" }}
										/>
									</div>
								)}
								{showButton([AutomationStatus.STARTED]) && (
									<div className="col-auto p-0 m-0  d-flex align-items-start">
										<ItiButton
											icon="mdi mdi-pause"
											hint={t("automation.actions.pause.tooltip")}
											disabled={!!processingAction}
											onClick={() =>
												executeAction(onPause, t("automation.executing.pause"))
											}
											elementAttr={{ class: "btn btn-sm btn-transparent" }}
										/>
									</div>
								)}
								{showButton([
									AutomationStatus.STARTED,
									AutomationStatus.PAUSED,
								]) && (
									<div className="col-auto p-0 m-0  d-flex align-items-start">
										<ItiButton
											icon="mdi mdi-stop"
											hint={t("automation.actions.stop.tooltip")}
											disabled={!!processingAction}
											onClick={() =>
												executeAction(onStop, t("automation.executing.stop"))
											}
											elementAttr={{ class: "btn btn-sm btn-transparent" }}
										/>
									</div>
								)}
								{showButton([
									AutomationStatus.PAUSED,
									AutomationStatus.ERROR_PAUSED_EXHAUSTED_QUOTA,
								]) && (
									<div className="col-auto p-0 m-0  d-flex align-items-start">
										<ItiButton
											icon="mdi mdi-replay"
											hint={t("automation.actions.resume.tooltip")}
											disabled={!!processingAction}
											onClick={() =>
												executeAction(
													onResume,
													t("automation.executing.resume"),
												)
											}
											elementAttr={{ class: "btn btn-sm btn-transparent" }}
										/>
									</div>
								)}
							</div>
						)}

						{automation.sessionId && automation.datasetId && (
							<div className="col-auto px-0 mx-0 ">
								<ItiButton
									className="automation-dataset-chip"
									text={t("automation.see-dataset")}
									icon="mdi mdi-book-open-page-variant"
									onClick={() => handleDatasetReferenced(automation)}
								/>
							</div>
						)}
					</div>

					<div className="row">
						{isError && (
							<span className="ms-1 mb-2 mt-1 text-danger">
								{getErrorMessage()}
							</span>
						)}
					</div>

					{/* Metadata */}
					<div className="row size-owner-visibility-automations mt-1 mb-0 pb-0 px-1">
						<div className="col">
							<div className="row">
								<div className="col-12 d-flex owner-container p-0 m-0 align-items-center">
									<i
										className="mdi mdi-calendar-clock me-1"
										title={t("automation.createdDate.tooltip")}
									/>
									<div className="date-text-content">
										{typeof automation.createdDate === "number"
											? formatTimestampWithLocale(
													automation.createdDate * 1000,
													language,
													t("catalogue.dateFormat.atTime"),
												)
											: "-"}
									</div>
								</div>

								{/* Type-specific information */}
								{getTypeSpecificInfo().map((info, index) => (
									<div key={index} className="col-auto owner-container p-0 m-0">
										<div className="owner-content">
											<i
												className={`mdi ${info.icon} me-1`}
												title={info.tooltip}
											/>
											<div className="date-text-content" title={info.tooltip}>
												{info.label}
											</div>
										</div>
									</div>
								))}

								{isStreaming && (
									<div className="col-auto owner-container">
										<div className="owner-content">
											<i
												className={`mdi ${getFrequencyIcon()} me-1`}
												title={getFrequencyTooltip()}
											/>
											<div
												className="date-text-content"
												title={getFrequencyTooltip()}
											>
												{getFrequencyLabel()}
											</div>
										</div>
									</div>
								)}

								<div className="col-auto owner-container  p-0 m-0">
									<div className="owner-content">
										<i
											className="mdi mdi-home-city-outline me-1"
											title={t("automation.organization.tooltip")}
										/>
										<div
											className="owner-text"
											title={automation.organizationName || "-"}
										>
											{automation.organizationName || "-"}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Description and action button */}
					<div className="row">
						<div
							className="col ps-2 content-card-description automation-description"
							title={automation.description || t("automation.noDescription")}
							style={
								!automation.description ? { fontStyle: "italic" } : undefined
							}
						>
							{automation.description || t("automation.noDescription")}
						</div>
						<div className="col-auto automation-chevron-container justify-content-end pe-0 me-0">
							<ItiButton
								className="content-chevron-right-icon-automations"
								text=">"
								hint={t("automation.viewDetails.tooltip")}
								onClick={onViewDetail}
							/>
						</div>
					</div>
				</div>
			</div>

			{processingAction && (
				<div className="automation-processing">
					<span className="dot-flashing" />
					<span>{processingAction}</span>
				</div>
			)}
		</div>
	);
}
